import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth, API } from "../lib/auth";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function InboxPage() {
  const { authHeader, user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);

  const loadThreads = async () => {
    const r = await axios.get(`${API}/messages/threads`, { headers: authHeader });
    setThreads(r.data || []);
    if (!activeId && r.data && r.data[0]) setActiveId(r.data[0].thread_id);
  };

  const loadMessages = async (tid) => {
    if (!tid) return;
    const r = await axios.get(`${API}/messages/${tid}`, { headers: authHeader });
    setMessages(r.data || []);
    setTimeout(() => { if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight; }, 50);
  };

  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeId) loadMessages(activeId); /* eslint-disable-next-line */ }, [activeId]);

  const send = async () => {
    if (!text.trim() || !activeId) return;
    const active = threads.find(t => t.thread_id === activeId);
    if (!active) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { to_user_id: active.other.id, text }, { headers: authHeader });
      setText(""); loadMessages(activeId); loadThreads();
    } catch (e) { toast.error("Send failed"); }
    setSending(false);
  };

  const active = threads.find(t => t.thread_id === activeId);

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]" data-testid="inbox-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-6 inline-flex items-center gap-2"><MessageSquare className="h-6 w-6 text-[#1A8FE3]" />Inbox</h1>
        <Card className="overflow-hidden grid grid-cols-12 h-[600px]">
          <div className="col-span-4 border-r border-slate-200 overflow-y-auto">
            {threads.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">No conversations yet.</div>
            ) : threads.map(thr => (
              <button
                key={thr.thread_id}
                onClick={() => setActiveId(thr.thread_id)}
                data-testid={`thread-${thr.thread_id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${activeId === thr.thread_id ? "bg-sky-50" : ""}`}
              >
                <div className="flex justify-between items-baseline">
                  <span className="font-medium text-slate-900 truncate">{thr.other?.company_name || thr.other?.name || "Unknown"}</span>
                  {thr.unread > 0 && <span className="ml-2 h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{thr.unread}</span>}
                </div>
                <div className="text-xs text-slate-500 truncate mt-1">{thr.last_text}</div>
              </button>
            ))}
          </div>
          <div className="col-span-8 flex flex-col">
            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
              {messages.map(m => (
                <div key={m.id} className={`flex ${m.from_id === user?.id ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${m.from_id === user?.id ? "bg-[#1A8FE3] text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"}`}>
                    {m.text}
                    <div className={`text-[10px] mt-1 ${m.from_id === user?.id ? "text-sky-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 p-3 bg-white flex gap-2">
              <Textarea data-testid="msg-text" value={text} onChange={(e) => setText(e.target.value)} placeholder={active ? `Write to ${active.other?.company_name || active.other?.name}…` : "Select a conversation"} rows={2} className="flex-1" />
              <Button onClick={send} disabled={!text.trim() || sending || !activeId} data-testid="msg-send" className="bg-[#1A8FE3] hover:bg-[#157AC0] rounded-full text-white">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
